import { Injectable, Signal, computed, signal } from "@angular/core";
import { PaletteConfig } from "../models/palette.model";
import { ThemeService } from "./theme.service";
import { BUILT_IN_PALETTES } from "../defaults/palette.default";

/**
 * Palette Service
 * 
 * Manages color palettes throughout the application. This service handles:
 * 1. Storing available palettes
 * 2. Selecting and applying palettes
 * 3. Converting between palette IDs and color values
 * 4. Applying color variables to CSS
 * 
 * Works in tandem with ThemeService - palette colors depend on the active theme mode
 * (light/dark) managed by ThemeService.
 */
@Injectable({
  providedIn: "root",
})
export class PaletteService {
  /**
   * Signal that stores all available palettes
   * Palettes contain both light and dark mode color variations
   */
  private availablePalettesSignal = signal<PaletteConfig[]>([]);

  /**
   * Signal for the currently selected palette ID
   * Format: 'pal_[unique identifier]'
   */
  private selectedPaletteIdSignal = signal<string | null>(null);

  /**
   * Computed signal that provides the currently selected palette object
   */
  public selectedPaletteSignal = computed(() => {
    const paletteId = this.selectedPaletteIdSignal();
    const palettes = this.availablePalettesSignal();
    
    if (!paletteId || palettes.length === 0) return null;
    
    return palettes.find(palette => palette.paletteId === paletteId) || null;
  });

  constructor(private themeService: ThemeService) {}

  /**
   * Update the collection of available palettes
   * @param palettes Array of palette objects
   */
  public setAvailablePalettes(palettes: PaletteConfig[]): void {
    if (!palettes || palettes.length === 0) return;
    
    this.availablePalettesSignal.set(palettes);
    
    // If no palette is selected yet, select the first one
    if (!this.selectedPaletteIdSignal() && palettes.length > 0) {
      this.selectPaletteById(palettes[0].paletteId);
    }
  }

  /**
   * Get all available palettes
   * @returns Signal with the array of all palettes
   */
  public getAvailablePalettesSignal(): Signal<PaletteConfig[]> {
    return this.availablePalettesSignal;
  }

  /** Append a user-created palette if its id is not already listed. */
  public appendAvailablePalette(palette: PaletteConfig): void {
    const current = this.availablePalettesSignal();
    if (current.some(p => p.paletteId === palette.paletteId)) {
      return;
    }
    this.availablePalettesSignal.set([...current, palette]);
  }

  /** Replace an existing palette by id, or append if missing (e.g. stale cache). */
  public patchAvailablePalette(palette: PaletteConfig): void {
    const current = this.availablePalettesSignal();
    const idx = current.findIndex(p => p.paletteId === palette.paletteId);
    if (idx < 0) {
      this.appendAvailablePalette(palette);
      return;
    }
    const existing = current[idx];
    // Skip when light/dark swatches are unchanged (avoids chart repaint on hydrate).
    // Label-only catalog updates are not applied when colors match.
    if (existing && this.#paletteColorsEqual(existing, palette)) {
      return;
    }
    const next = [...current];
    next[idx] = palette;
    this.availablePalettesSignal.set(next);
  }

  #paletteColorsEqual(left: PaletteConfig, right: PaletteConfig): boolean {
    if (left.paletteId !== right.paletteId) {
      return false;
    }
    if (left.light.length !== right.light.length) {
      return false;
    }
    if (left.dark.length !== right.dark.length) {
      return false;
    }
    return (
      left.light.every((color, index) => color === right.light[index]) &&
      left.dark.every((color, index) => color === right.dark[index])
    );
  }

  /**
   * Select a palette by its ID
   * @param paletteId ID of the palette to select
   * @returns boolean indicating success
   */
  public selectPaletteById(paletteId: string): boolean {
    if (!paletteId) return false;
    
    // Check if this is a palette ID (starts with 'pal_')
    if (!paletteId.startsWith('pal_')) {
      // This might be a direct color reference, not a palette ID
      return false;
    }
    
    const palettes = this.availablePalettesSignal();
    const palette = palettes.find(p => p.paletteId === paletteId);
    
    if (!palette) return false;
    
    this.selectedPaletteIdSignal.set(paletteId);
    
    // Apply the palette colors to the theme service
    const themeMode = this.themeService.getActiveThemeModeSignal()();
    this.applyPaletteToTheme(palette, themeMode);
    
    return true;
  }

  /**
   * Apply colors directly (when colors are provided instead of a palette ID)
   * @param colors Array of color values
   * @returns boolean indicating success
   */
  public applyDirectColors(colors: string[]): boolean {
    if (!colors || colors.length === 0) return false;
    
    // Apply these colors directly
    this.applyCssColorVariables(colors);
    
    return true;
  }

  /**
   * Process colors field which could be palette IDs or direct colors
   * @param colors The colors field from a component or config
   * @returns boolean indicating if processing was successful
   */
  public processColorsField(colors: string | string[]): boolean {
    if (!colors) return false;
    
    // If colors is a string, it might be a single palette ID
    if (typeof colors === 'string') {
      // If it looks like a palette ID, use it
      if (colors.startsWith('pal_')) {
        return this.selectPaletteById(colors);
      }
      // Otherwise, treat it as a single color
      return this.applyDirectColors([colors]);
    }
    
    // If array but empty
    if (colors.length === 0) return false;
    
    // If the first item looks like a palette ID
    if (colors[0].startsWith('pal_')) {
      return this.selectPaletteById(colors[0]);
    }
    
    // Otherwise, treat the array as direct colors
    return this.applyDirectColors(colors);
  }

  /**
   * Get the ID of the currently selected palette
   * @returns Signal with the currently selected palette ID
   */
  public getSelectedPaletteIdSignal(): Signal<string | null> {
    return this.selectedPaletteIdSignal;
  }

  /**
   * Get the currently selected palette
   * @returns The currently selected palette or null
   */
  public getCurrentPalette(): PaletteConfig | null {
    return this.selectedPaletteSignal();
  }

  /**
   * Get palette by ID
   * @param paletteId The ID of the palette to retrieve
   * @returns The requested palette or null if not found
   */
  public getPaletteById(paletteId: string): PaletteConfig | null {
    if (!paletteId || !paletteId.startsWith('pal_')) return null;
    
    const palettes = this.availablePalettesSignal();
    const localPalette = palettes.find(p => p.paletteId === paletteId);
    if (localPalette) return localPalette;

    // Fallback for runtime paths that use built-in IDs before explicit initialization.
    return BUILT_IN_PALETTES.find(p => p.paletteId === paletteId) || null;
  }

  /**
   * Apply palette to the current theme
   * @param palette The palette to apply
   * @param themeMode The current theme mode (light or dark)
   */
  private applyPaletteToTheme(palette: PaletteConfig, themeMode: 'light' | 'dark'): void {
    // Create CSS variables for the palette colors
    const colors = themeMode === 'dark' ? palette.dark : palette.light;
    this.applyCssColorVariables(colors);
    
    // Optionally call themeService method if it has one
    this.themeService.applyPalette(palette.paletteId);
  }

  /**
   * Apply CSS variables for palette colors
   * @param colors Array of color values to apply
   */
  private applyCssColorVariables(colors: string[]): void {
    const root = document.documentElement;
    
    colors.forEach((color, index) => {
      root.style.setProperty(`--palette-color-${index}`, color);
    });
    
    root.style.setProperty("--chart-colors", colors.join(", "));
  }

  /**
   * Initialize palette service with palettes from the API
   * @param palettes Array of palettes from the API
   * @param defaultPaletteId Optional ID of palette to select by default
   */
  public initWithPalettes(palettes: PaletteConfig[], defaultPaletteId?: string): void {
    this.setAvailablePalettes(palettes);
    
    if (defaultPaletteId) {
      this.selectPaletteById(defaultPaletteId);
    }
  }

  /**
   * Get colors for the current palette in the active theme mode
   * @returns Array of color strings
   */
  public getCurrentColors(): string[] {
    const palette = this.selectedPaletteSignal();
    if (!palette) return [];
    
    const themeMode = this.themeService.getActiveThemeModeSignal()();
    return themeMode === 'dark' ? palette.dark : palette.light;
  }

  /**
   * Get colors for a specific palette ID
   * @param paletteId The palette ID to get colors for
   * @returns Array of color strings
   */
  public getColorsForPalette(
    paletteId: string,
    resolvedThemeMode?: 'light' | 'dark'
  ): string[] {
    const palette = this.getPaletteById(paletteId);
    if (!palette) return [];

    const themeMode =
      resolvedThemeMode ?? this.themeService.getActiveThemeModeSignal()();
    return themeMode === 'dark' ? palette.dark : palette.light;
  }

  /**
   * Get colors for the current theme from a palette ID with optional per-theme overrides.
   * Used when config has palette.selectedPaletteId and optional customLight/customDark.
   * @param paletteId The palette ID (e.g. from config.palette.selectedPaletteId)
   * @param customLight Optional override colors for light theme
   * @param customDark Optional override colors for dark theme
   * @param resolvedThemeMode When set (e.g. chart `themeMode` is light/dark), picks overrides by this mode instead of ThemeService — needed when the palette id is not in the registry but the chart has an explicit preview theme.
   * @returns Array of color strings for the active theme
   */
  public getColorsForPaletteWithOverrides(
    paletteId: string,
    customLight?: string[],
    customDark?: string[],
    resolvedThemeMode?: 'light' | 'dark'
  ): string[] {
    const themeMode =
      resolvedThemeMode ?? this.themeService.getActiveThemeModeSignal()();
    if (themeMode === 'dark' && customDark?.length) return customDark;
    if (themeMode === 'light' && customLight?.length) return customLight;
    return this.getColorsForPalette(paletteId);
  }

  /**
   * Get colors for a specific palette
   * @param paletteId The ID or name of the palette
   * @returns Array of color strings for current theme mode
   * 
   * This method is the primary way for components to get palette colors.
   * It handles both palette IDs (starting with 'pal_') and palette names,
   * providing appropriate colors for the current theme mode.
   */
  public getColorPalette(
    paletteId: string,
    resolvedThemeMode?: 'light' | 'dark'
  ): string[] {
    if (!paletteId) return [];
    
    // If this is a palette ID, get colors for it
    if (paletteId.startsWith('pal_')) {
      return this.getColorsForPalette(paletteId, resolvedThemeMode);
    }
    
    // Otherwise, try to find palette by name
    const palettes = this.availablePalettesSignal();
    const palette = palettes.find(p => p.label === paletteId);
    
    if (palette) {
      return this.getColorsForPalette(palette.paletteId, resolvedThemeMode);
    }
    
    // Return fallback colors if no palette found
    return ["#4C9AFF", "#F66A0A", "#00C7E6", "#36B37E", "#6554C0", "#FFAB00", "#FF5630"];
  }
} 